<template>
  <transition name="coverage-banner">
    <section
      v-if="visible"
      ref="banner"
      class="coverage-banner"
      data-testid="coverage-banner"
      role="alert"
      aria-labelledby="coverage-alert-title"
      :aria-describedby="step === 'prompt' ? 'coverage-alert-desc' : null"
      @keydown="handleKeydown"
    >
      <transition name="coverage-step" mode="out-in">
        <div v-if="step === 'prompt'" key="prompt" class="coverage-inner coverage-inner--prompt">
          <div class="coverage-context">
            <p id="coverage-alert-desc" class="coverage-status bz-chip bz-chip--warn-solid">
              <BzIcon name="photo_camera" />
              <i18n-t
                keypath="message.coverageAlertStatus"
                :plural="count"
                scope="global"
                tag="span"
                class="coverage-status-text"
              >
                <template #count>
                  <strong class="coverage-count" data-testid="coverage-count">{{ count }}</strong>
                </template>
              </i18n-t>
            </p>
            <p id="coverage-alert-title" class="coverage-title">
              {{ $t('message.coverageAlertTitle') }}
            </p>
          </div>

          <div class="coverage-actions">
            <button class="cov-primary" data-testid="coverage-ack" @click="$emit('ack')">
              <BzIcon name="photo_camera" />
              <span>{{ $t('message.coverageAlertAck') }}</span>
            </button>

            <button class="cov-secondary" data-testid="coverage-cant" @click="showReasons">
              <span>{{ $t('message.coverageAlertCant') }}</span>
              <BzIcon name="chevron_right" />
            </button>
          </div>
        </div>

        <div v-else key="reasons" class="coverage-inner coverage-inner--choice">
          <div class="coverage-choice-head">
            <button class="coverage-back" data-testid="coverage-back" @click="showPrompt">
              <BzIcon name="chevron_left" />
              <span>{{ $t('message.coverageAlertBack') }}</span>
            </button>
            <p id="coverage-alert-title" class="coverage-title coverage-title--sub">
              {{ $t('message.coverageAlertReasonTitle') }}
            </p>
          </div>

          <div class="coverage-reasons">
            <button class="bz-tile bz-tile--row cov-reason" data-testid="coverage-reason-busy" @click="$emit('busy')">
              <BzIcon name="schedule" class="bz-tile__icon" />
              <span class="reason-copy">
                <span class="bz-tile__label">{{ $t('message.coverageAlertBusy') }}</span>
                <span class="reason-hint">{{ $t('message.coverageAlertBusyHint') }}</span>
              </span>
            </button>

            <button class="bz-tile bz-tile--row cov-reason" data-testid="coverage-reason-support" @click="$emit('support')">
              <BzIcon name="headset_mic" class="bz-tile__icon" />
              <span class="reason-copy">
                <span class="bz-tile__label">{{ $t('message.coverageAlertSupport') }}</span>
                <span class="reason-hint">{{ $t('message.coverageAlertSupportHint') }}</span>
              </span>
            </button>
          </div>
        </div>
      </transition>

      <button class="coverage-close" data-testid="coverage-dismiss" :aria-label="$t('message.coverageAlertDismiss')" @click="$emit('dismiss', 'button')">
        <BzIcon name="close" />
      </button>

      <!-- Cuenta atrás del auto-cierre: una pista fina, la del sistema -->
      <div class="coverage-progress" aria-hidden="true">
        <div class="coverage-progress-fill" :style="progressStyle()"></div>
      </div>
    </section>
  </transition>
</template>

<script>
import BzIcon from '@/ui/components/BzIcon.vue';

export default {
  name: 'PhotoCoverageAlertComponent',
  components: { BzIcon },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    count: {
      type: Number,
      default: 0
    },
    // Cuánto vive el banner antes de auto-cerrarse y cuándo se armó (o re-armó, al navegar
    // de paso) ese timer. El timer real vive en App.vue; aquí solo se pinta la barra de
    // cuenta atrás, y este par la mantiene sincronizada aunque el banner se re-monte a
    // mitad (p.ej. al volver de una foto).
    autoDismissMs: {
      type: Number,
      default: 120000
    },
    timerStartedAt: {
      type: Number,
      default: 0
    }
  },
  emits: ['ack', 'busy', 'support', 'dismiss', 'step-change'],
  data() {
    return {
      step: 'prompt'
    };
  },
  watch: {
    visible: {
      immediate: true,
      handler(isVisible) {
        this.step = 'prompt';
        // No es un modal: nunca roba el foco ni atrapa el tabulador. El operario sigue
        // usando la app; cualquier toque fuera del banner lo cierra (y el toque llega
        // igualmente a su destino: no se hace preventDefault).
        if (isVisible) {
          document.addEventListener('pointerdown', this.onOutsidePointerDown, true);
        } else {
          document.removeEventListener('pointerdown', this.onOutsidePointerDown, true);
        }
      }
    }
  },
  beforeUnmount() {
    document.removeEventListener('pointerdown', this.onOutsidePointerDown, true);
  },
  methods: {
    // Método (no computed) a propósito: Date.now() no es reactivo, y una computed cachearía
    // el primer render — al re-mostrarse tras una foto la barra saldría llena mintiendo el
    // tiempo restante. Cada render del v-if la evalúa fresca.
    progressStyle() {
      const elapsed = this.timerStartedAt ? Math.max(0, Date.now() - this.timerStartedAt) : 0;
      return {
        animationDuration: `${this.autoDismissMs}ms`,
        animationDelay: `-${Math.min(elapsed, this.autoDismissMs)}ms`
      };
    },
    showReasons() {
      this.step = 'reasons';
      this.$emit('step-change', this.step);
    },
    showPrompt() {
      this.step = 'prompt';
      this.$emit('step-change', this.step);
    },
    onOutsidePointerDown(event) {
      const banner = this.$refs.banner;
      if (banner && banner.contains(event.target)) return;
      // Overlays that stack ABOVE this banner (rider toasts z10000, blocking notification
      // z9999): a tap there is answering THAT surface, possibly without the banner even
      // being visible underneath — it must not burn this alert as "dismissed".
      if (event.target instanceof Element
          && event.target.closest('.rider-toast-container, .notification-overlay')) return;
      // 'outside' = toque incidental (estaban usando la app); el ✕/Escape es deliberado.
      // App.vue solo devuelve el slot de presupuesto en el incidental.
      this.$emit('dismiss', 'outside');
    },
    handleKeydown(event) {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      if (this.step === 'reasons') {
        this.showPrompt();
      } else {
        this.$emit('dismiss', 'button');
      }
    }
  }
};
</script>

<style scoped>
/* Attached to the top edge: only its free corners are round, on the raised surface of what floats, with its
   edge and the short shadow (docs/sistema-diseno/forma-y-tamano.md, «Banner de cobertura»). */
.coverage-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: var(--bz-z-coverage-banner);
  box-sizing: border-box;
  /* Safe-area insets ADDED to the padding, not max()'d (same rationale as the old sheet:
     the kitchen tablets report 0 while the system bar still overlays the page). The bottom
     padding leaves the lane of the countdown line. */
  padding:
    calc(env(safe-area-inset-top, 0px) + var(--bz-space-6))
    calc(env(safe-area-inset-right, 0px) + var(--bz-gutter) + var(--bz-target-min))
    var(--bz-space-8)
    calc(env(safe-area-inset-left, 0px) + var(--bz-gutter));
  background: var(--bz-raised);
  color: var(--bz-ink);
  border: 1px solid var(--bz-edge);
  border-top: 0;
  border-radius: 0 0 var(--bz-radius-lg) var(--bz-radius-lg);
  box-shadow: var(--bz-shadow-overlay);
  font-family: var(--bz-font);
  font-optical-sizing: auto;
  font-synthesis: none;
  font-kerning: normal;
}

/* misma altura mínima en ambos pasos para que el banner no salte al cambiar de paso */
.coverage-inner {
  width: min(1120px, 100%);
  min-height: calc(var(--bz-target-primary) + var(--bz-space-6));
  margin: 0 auto;
}

/* paso 1: contexto + acciones en una fila; si no caben, las acciones bajan enteras en vez
   de estrujar el chip (en francés "4 dernières commandes sans photo" se partía en dos). */
.coverage-inner--prompt {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--bz-space-4) var(--bz-space-7);
}

.coverage-context { flex: 1 1 auto; min-width: 0; }

/* paso 2: cabecera (volver + pregunta) | motivos a la derecha */
/* Sin breakpoint por idioma: la cabecera NO se comprime (así la pregunta nunca se parte) y
   los motivos bajan a su propia fila en cuanto no les quedan ~560 px. En francés la pregunta
   y "La tablette a un problème" son bastante más largas que en español, y con un ancho fijo
   siempre había una franja donde el título se rompía en dos líneas. */
.coverage-inner--choice {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--bz-space-4) var(--bz-space-7);
}

/* ---- paso 1: contexto ---- */
/* La evidencia es una etiqueta de aviso de la norma 11 (.bz-chip--warn, del sistema): lleva su palabra
   y su icono, y no es un botón. */
.coverage-status {
  margin: 0;
}

.coverage-count {
  font-weight: inherit;
}

/* 28/36 a 600: el nombre de lo que se pregunta. */
.coverage-title {
  margin: var(--bz-space-3) 0 0;
  color: var(--bz-ink);
  font-family: var(--bz-font-display);
  font-size: var(--bz-text-3xl);
  line-height: var(--bz-leading-3xl);
  letter-spacing: var(--bz-tracking-3xl);
  font-weight: var(--bz-weight-label);
  text-wrap: balance;
}

.coverage-title--sub {
  margin: 0;
}

/* ---- paso 1: acciones ---- */
.coverage-actions {
  flex: 0 0 auto;   /* los botones nunca se comprimen: son el objetivo táctil */
  display: flex;
  align-items: stretch;
  gap: var(--bz-target-gap);
}

.cov-primary,
.cov-secondary,
.cov-reason,
.coverage-back,
.coverage-close {
  font: inherit;
  cursor: pointer;
  user-select: none;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

/* 72 con sus esquinas de 16: la acción primaria (azul del sistema), y la única rellena de azul. */
.cov-primary,
.cov-secondary {
  box-sizing: border-box;
  min-height: var(--bz-target-primary);
  padding: 0 var(--bz-space-6);
  border: 0;
  border-radius: var(--bz-radius-lg);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--bz-space-2);
  font-size: var(--bz-text-lg);
  line-height: var(--bz-leading-lg);
  letter-spacing: var(--bz-tracking-lg);
  font-weight: var(--bz-weight-label);
}

.cov-primary {
  background: var(--bz-action);
  color: var(--bz-on-action);
}

.cov-primary .bz-icon,
.cov-secondary .bz-icon {
  font-size: var(--bz-icon-md);
}

.cov-primary span,
.cov-secondary span { text-wrap: balance; }

/* la salida: el gris de control, sin relleno de azul */
.cov-secondary {
  background: var(--bz-control);
  color: var(--bz-ink);
}

/* ---- cierre explícito (el toque fuera hace lo mismo): redondo de 48, sin relleno ---- */
.coverage-close {
  position: absolute;
  top: calc(env(safe-area-inset-top, 0px) + var(--bz-space-4));
  right: calc(env(safe-area-inset-right, 0px) + var(--bz-space-3));
  width: var(--bz-target-min);
  height: var(--bz-target-min);
  padding: 0;
  border: 0;
  border-radius: var(--bz-radius-full);
  display: grid;
  place-items: center;
  background: transparent;
  color: var(--bz-ink-2);
  font-size: var(--bz-icon-md);
}

/* ---- paso 2 ---- */
.coverage-choice-head {
  flex: 0 0 auto;   /* nunca cede ancho: es lo que protege a la pregunta de partirse */
  min-width: 0;
  display: flex;
  align-items: center;
  gap: var(--bz-space-4);
}

/* «Volver»: una cápsula de 48, como los botones de la barra */
.coverage-back {
  box-sizing: border-box;
  min-height: var(--bz-target-min);
  padding: 0 var(--bz-space-4) 0 var(--bz-space-2);
  border: 0;
  border-radius: var(--bz-radius-full);
  display: inline-flex;
  align-items: center;
  gap: var(--bz-space-1);
  background: var(--bz-control);
  color: var(--bz-ink);
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
  letter-spacing: var(--bz-tracking-md);
  font-weight: var(--bz-weight-label);
  flex: 0 0 auto;
}

.coverage-back .bz-icon {
  font-size: var(--bz-icon-md);
}

/* Dos motivos gemelos, lado a lado: las baldosas en fila del sistema (96 de alto) */
.coverage-reasons {
  /* 660 px = las dos baldosas a 320 + el hueco: si no caben así en la fila de la cabecera,
     bajan enteras y usan todo el ancho (dos columnas), que queda más bajo que una columna. */
  flex: 1 1 660px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: var(--bz-target-gap);
}

.cov-reason {
  width: 100%;
  cursor: pointer;
}

.reason-copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.reason-hint {
  color: var(--bz-ink-2);
  font-size: var(--bz-text-sm);
  line-height: var(--bz-leading-sm);
  letter-spacing: var(--bz-tracking-sm);
  font-weight: var(--bz-weight-body);
}

.cov-primary:focus-visible,
.cov-secondary:focus-visible,
.cov-reason:focus-visible,
.coverage-back:focus-visible,
.coverage-close:focus-visible {
  outline: 3px solid var(--bz-focus);
  outline-offset: 2px;
}

/* ---- cuenta atrás del auto-cierre ---- */
/* Una pista fina, alineada con el margen de la pantalla: dice cuánto queda (sigue con «reducir movimiento»). */
.coverage-progress {
  position: absolute;
  bottom: var(--bz-space-4);
  left: calc(env(safe-area-inset-left, 0px) + var(--bz-gutter));
  right: calc(env(safe-area-inset-right, 0px) + var(--bz-gutter));
  height: var(--bz-space-1);
  border-radius: var(--bz-radius-full);
  overflow: hidden;
  background: var(--bz-control);
}

.coverage-progress-fill {
  width: 100%;
  height: 100%;
  background: var(--bz-track-active);
  transform-origin: left center;
  animation-name: coverage-drain;
  animation-timing-function: linear;
  animation-fill-mode: forwards;
}

@keyframes coverage-drain {
  from { transform: scaleX(1); }
  to { transform: scaleX(0); }
}

/* ---- entrada/salida: opacity siempre; el desplazamiento, solo sin «reducir movimiento».
   Entra más despacio de lo que sale (--bz-duration entra, --bz-duration-fast sale) y frena sin rebotar. ---- */
.coverage-banner-enter-active {
  transition: opacity var(--bz-duration) var(--bz-ease-out);
}

.coverage-banner-leave-active {
  transition: opacity var(--bz-duration-fast) var(--bz-ease-out);
  /* Mientras sale, que el banner moribundo no se trague los toques del contenido de debajo */
  pointer-events: none;
}

.coverage-banner-enter-from,
.coverage-banner-leave-to {
  opacity: 0;
}

.coverage-step-enter-active {
  transition: opacity var(--bz-duration-fast) var(--bz-ease-out);
}

.coverage-step-leave-active {
  transition: opacity var(--bz-duration-fast) var(--bz-ease-out);
}

.coverage-step-enter-from,
.coverage-step-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: no-preference) {
  .coverage-banner-enter-active {
    transition: opacity var(--bz-duration) var(--bz-ease-out), transform var(--bz-duration) var(--bz-ease-out);
  }

  .coverage-banner-leave-active {
    transition: opacity var(--bz-duration-fast) var(--bz-ease-out), transform var(--bz-duration-fast) var(--bz-ease-out);
  }

  .coverage-banner-enter-from,
  .coverage-banner-leave-to {
    transform: translateY(calc(var(--bz-space-6) * -1));
  }

  .coverage-step-enter-active,
  .coverage-step-leave-active {
    transition: opacity var(--bz-duration-fast) var(--bz-ease-out), transform var(--bz-duration-fast) var(--bz-ease-out);
  }

  .coverage-step-enter-from {
    transform: translateX(var(--bz-space-2));
  }

  .coverage-step-leave-to {
    transform: translateX(calc(var(--bz-space-2) * -1));
  }
}

/* ---- responsive ---- */
/* Apila por debajo de 1000 px: en una fila el paso 2 necesita ~930 px (Volver + pregunta +
   dos motivos + padding), y por debajo de eso la pregunta se comprimía hasta partirse en
   tres líneas y pisar los botones. */
@media screen and (max-width: 1000px) {
  .coverage-banner {
    /* Padding lateral SIMÉTRICO: aquí los botones van a ancho completo y el hueco del ✕
       solo estorba en la primera fila, así que lo reserva el bloque de contexto. */
    padding:
      calc(env(safe-area-inset-top, 0px) + var(--bz-space-4))
      calc(env(safe-area-inset-right, 0px) + var(--bz-gutter))
      var(--bz-space-8)
      calc(env(safe-area-inset-left, 0px) + var(--bz-gutter));
  }

  /* El ✕ está en la esquina superior derecha y solo comparte fila con el chip/cabecera */
  .coverage-context,
  .coverage-choice-head { padding-right: var(--bz-target-min); }

  .coverage-inner { min-height: 0; }
  .coverage-inner--prompt,
  .coverage-inner--choice { gap: var(--bz-target-gap); }

  .coverage-choice-head {
    flex: 1 1 100%;   /* apilado: puede ceder ancho y su flex-wrap interno actúa de verdad */
    flex-wrap: wrap;
    column-gap: var(--bz-space-4);
    row-gap: var(--bz-space-1);
  }

  .coverage-context,
  .coverage-actions { flex-basis: 100%; }
  /* Apilado: los dos botones comparten fila si caben (a 260 cada uno) y, si no, bajan uno a uno a ancho completo */
  .coverage-actions { flex-wrap: wrap; }
  .cov-primary,
  .cov-secondary { flex: 1 1 260px; }
  /* apilado: sobra alto y falta ancho, así que el chip ya puede romper si hace falta */
  .coverage-status { white-space: normal; }
  .coverage-reasons { flex-basis: 100%; }
}

/* Un móvil: el nombre baja un paso (22/28), el del diálogo */
@media screen and (max-width: 599px) {
  .coverage-title {
    font-size: var(--bz-text-xl);
    line-height: var(--bz-leading-xl);
    letter-spacing: var(--bz-tracking-xl);
  }
}
</style>
